import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { encodePico8GfxClipboard } from '../lib/pico8Clipboard';
import MiniPreview from './MiniPreview';
import PreviewOverlayPortal from './PreviewOverlayPortal';
import EditorTools from './EditorTools';
import ColorPalette from './ColorPalette';
import PixelGrid from './PixelGrid';
import { useResponsive } from '../hooks/useResponsive';
import { usePainting } from '../hooks/usePainting';
import AuthModal from './AuthModal';
import SaveDialog from './SaveDialog';
import { useSpriteSave } from '../hooks/useSpriteSave';
import MatchPanelHeights from './MatchPanelHeights';
import pb from '../lib/pb'; // NYTT: behövs för authStore
import { PICO8_SECONDARY } from '../utils/palette';

interface EditorTabProps {
  pixels: number[][] | null;
  colors: string[];
  selectedColorIndex: number;
  onPixelClick: (x: number, y: number) => void;

  // NYTT
  previewPixels: number[][];
  savePixels: number[][];
  onColorSelect: (index: number) => void;
  onCreateEmpty: () => void;
  onClear: () => void;
  onRotate: () => void;
  onFlip: () => void;
  onShiftRight?: () => void; 
  onShiftDown?: () => void; // NEW
  onSaved?: () => void;                 // callback efter lyckad save
  onStrokeStart?: () => void;
  onStrokeEnd?: () => void;
  onMergeP8?: (file: File) => void;
  gridSize: 8 | 16;
    isQuadSelectMode: boolean;
  onChooseQuadrant: (q: 0 | 1 | 2 | 3) => void;
  onBeginQuadSelect: () => void;
  onToggleGridSize: () => void;


  // NYTT för mobil preview
  overlayPixels: number[][];      // 16×16 från hooken
  mobilePreviewVisible: boolean;  // synlighetsflagga

  // NYTT: undo/redo
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;

  // NYTT: anropas när Save kräver inloggning
  onRequireAuth?: () => void;

  // NEW
  onFillAt?: (x: number, y: number) => void;
}

export default function EditorTab(props: EditorTabProps) {
  const { pixels, colors, onRequireAuth, onCreateEmpty } = props;
  const isMobile = useResponsive();
  const [toolsOpen, setToolsOpen] = React.useState(false);

  useEffect(() => {
    if (!pixels) onCreateEmpty();
  }, [pixels, onCreateEmpty]);


  // Använd hooken som enda källa och skicka vidare onSaved till hooken
  const { saving, canSave, handleSaveClick, showAuth, handleAuthSuccess, closeAuth, showSaveDialog, closeSaveDialog, confirmSave } =
    useSpriteSave({ pixels: props.savePixels, colors, secondaryColors: PICO8_SECONDARY, onSaved: props.onSaved });

  // Ctrl/Cmd+S = Save
  const lastSaveTs = useRef(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // rätt tangent?
      const isSaveKey = (e.key === 's' || e.code === 'KeyS') && (e.ctrlKey || e.metaKey);
      if (!isSaveKey) return;

      // ignorera i skrivfält
      const t = e.target as HTMLElement | null;
      const isEditable =
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          (t as HTMLElement).isContentEditable);
      if (isEditable) return;

      // förhindra browserns "Save page"
      e.preventDefault();

      // enkel throttle så vi inte triggar flera gånger vid auto-repeat
      const now = Date.now();
      if (now - lastSaveTs.current < 600) return;
      lastSaveTs.current = now;

      if (canSave) handleSaveClick();
      // ev. else: visa en liten toast "Nothing to save"
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canSave, handleSaveClick]);

  // Ctrl/Cmd+C → copy current sprite for Pico-8
  useEffect(() => {
    const onCopyKey = async (e: KeyboardEvent) => {
      const isCopyKey = (e.key === 'c' || e.code === 'KeyC') && (e.ctrlKey || e.metaKey);
      if (!isCopyKey) return;

      // Ignore if typing in inputs/contenteditable
      const t = e.target as HTMLElement | null;
      const isEditable = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || (t as HTMLElement).isContentEditable);
      if (isEditable) return;

      try {
        const sprite = props.pixels;
        if (!sprite || sprite.length === 0) return;
        const encoded = encodePico8GfxClipboard(sprite);
        await navigator.clipboard.writeText(encoded);
        console.log('Copied sprite for Pico-8 ([gfx] tag). Try pasting in the PICO-8 sprite editor.');
        e.preventDefault();
      } catch (err) {
        console.warn('Clipboard write failed:', err);
      }
    };
    window.addEventListener('keydown', onCopyKey);
    return () => window.removeEventListener('keydown', onCopyKey);
  }, [props.pixels]);

  useEffect(() => { if (!isMobile) setToolsOpen(false); }, [isMobile]);

  const handleSave = async () => {
    try {
      if (!pb.authStore.isValid) {
        onRequireAuth?.(); // öppna Auth-modal i “save”-läge via App.tsx
        return;
      }
      // inloggad → kör ordinarie saveflöde
      handleSaveClick();
    } catch (e) {
      console.error('Save error:', e);
    }
  };

  //const wrapRef = useRef<HTMLDivElement | null>(null);
  //const gridRef = React.useRef<HTMLDivElement | null>(null);

  // Klick i overlay → beräkna kvadrant (0 TL, 1 TR, 2 BL, 3 BR)
  const handleQuadrantClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const rx = (e.clientX - rect.left) / rect.width;
    const ry = (e.clientY - rect.top) / rect.height;
    const qx = rx < 0.5 ? 0 : 1;
    const qy = ry < 0.5 ? 0 : 1;
    const q = (qy ? 2 : 0) + qx;
    props.onChooseQuadrant?.(q as 0 | 1 | 2 | 3); // stänger val-läge i hooken
  };

  const overlay =
    props.isQuadSelectMode && props.gridSize === 16 ? (
      <div className="quad-select-overlay" onClick={handleQuadrantClick}>
        <div className="quad-select-grid">
          <div className="qcell" />
          <div className="qcell" />
          <div className="qcell" />
          <div className="qcell" />
        </div>
        <div className="quad-select-label">Select quadrant</div>
      </div>
    ) : null;

  // Host för gridden (den yta som gridden ska följa)
  const gridHostRef = useRef<HTMLDivElement | null>(null);
  const [pixelSize16, setPixelSize16] = useState(24);

  useLayoutEffect(() => {
    const el = gridHostRef.current;
    if (!el) return;
    const compute = () => {
      const r = gridHostRef.current!.getBoundingClientRect();
      const side = Math.min(r.width, r.height);
      const safety = 2; // px
      const px = Math.max(1, Math.floor((side - safety) / 16));
      setPixelSize16(px);
    };
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    compute();
    return () => ro.disconnect();
  }, []);

  const showMobilePreview = isMobile && props.mobilePreviewVisible && props.gridSize === 8;

  // Tool: pen | fill
  const [tool, setTool] = useState<'pen' | 'fill'>('pen');
const toggleFill = React.useCallback(() => setTool(t => (t === 'fill' ? 'pen' : 'fill')), []);
const { onFillAt: fillAtProp, onPixelClick: pixelClickProp } = props;

  // Hitta pixel under pekare (.pixel-editable med data-x/y)
  const hitPixel = (clientX: number, clientY: number) => {
    const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    const px = el?.closest?.('.pixel-editable') as HTMLElement | null;
    if (!px) return null;
    const x = Number(px.dataset.x), y = Number(px.dataset.y);
    return Number.isNaN(x) || Number.isNaN(y) ? null : { x, y };
  };

  // Enhetlig klick (pen/fill)
const handleCanvasClick = React.useCallback((x: number, y: number) => {
  if (tool === 'fill') fillAtProp?.(x, y);
  else pixelClickProp?.(x, y);
}, [tool, fillAtProp, pixelClickProp]);

  // Målar endast i pen-läge. Fill körs en gång på pointer down (ingen stroke) → 1 undo.
  const { onPointerDown: paintingDown, onPointerMove, onPointerUp: paintingUp } = usePainting((cx, cy) => {
    const h = hitPixel(cx, cy);
    if (h) handleCanvasClick(h.x, h.y);
  });

  const handlePointerDown = (e: React.PointerEvent) => {
    const h = hitPixel(e.clientX, e.clientY);
    if (!h) return;
    if (tool === 'fill') {
      handleCanvasClick(h.x, h.y); // fyll direkt
      return;
    }
    props.onStrokeStart?.();
    paintingDown(e);
    handleCanvasClick(h.x, h.y); // måla första pixeln
  };

  const handlePointerUp = () => {
    if (tool === 'pen') {
      paintingUp();
      props.onStrokeEnd?.();
    }
  };

  // Hjälpare för index
const toBase = (v: number) => (v < 0 ? (v + 16) & 0x0f : (v & 0x0f));
const toSecondary = (base: number) => (base & 0x0f) | 0x80;

// Sekundär-palett (visuellt)
const [showSecondary, setShowSecondary] = React.useState(false);

// Destrukturera props som används i callbacks för att undvika ESLint-varningar
const { onColorSelect: onColorSelectProp, selectedColorIndex } = props;

// REMAP: när vi togglar palett, uppdatera valt index så ritning matchar vyn
const onToggleSecondary = React.useCallback(() => {
  const base = toBase(selectedColorIndex);
  const nextIdx = !showSecondary ? toSecondary(base) : base; // going to secondary => 128+base, to primary => base
  onColorSelectProp?.(nextIdx);
  setShowSecondary(v => !v);
}, [showSecondary, selectedColorIndex, onColorSelectProp]);

// Palett som visas
const paletteForView = showSecondary ? PICO8_SECONDARY : props.colors;

// Markerad ruta i vyn är alltid basindex (0..15)
const selectedIndexForView = toBase(selectedColorIndex);

// När man väljer i paletten: skriv 0..15 i primary, 128..143 i secondary
const handleSelectColor = React.useCallback(
  (i: number) => {
    const idx = showSecondary ? toSecondary(i) : (i & 0x0f);
    onColorSelectProp?.(idx);
  },
  [showSecondary, onColorSelectProp]
);

  return (
    <div className="editor-tab">
      {/* Mobil top‑bar: Undo/Redo till vänster, TOOLS-knapp till höger */}
      {isMobile && (
        <div className="mobile-topbar" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px' }}>
          <button className="mini-toggle" onClick={props.onToggleGridSize} title="Toggle 8×8 / 16×16">
            {props.gridSize === 8 ? '8×8' : '16×16'}
          </button>
          <button
            className="mini-toggle"
            onClick={props.onUndo}
            disabled={!props.canUndo}
            aria-label="Undo"
            title="Undo"
          >
            <span className="label">UNDO</span>
          </button>
          <button
            className="mini-toggle"
            onClick={props.onRedo}
            disabled={!props.canRedo}
            aria-label="Redo"
            title="Redo"
          >
            <span className="label">REDO</span>
          </button>

          <div style={{ marginLeft: 'auto' }}>
            {toolsOpen ? (
              <button
                className="mini-toggle mini-toggle-open"
                onClick={() => setToolsOpen(false)}
                aria-label="Close tools"
                aria-expanded="true"
              >
                ×
              </button>
            ) : (
              <button
                className="mini-toggle"
                onClick={() => setToolsOpen(true)}
                aria-label="Open tools"
                aria-expanded="false"
              >
                TOOLS
              </button>
            )}
          </div>
        </div>
      )}

      <div className="editor-layout">
        {!toolsOpen && (
          <div className="editor-sidebar">
            {/* Preview: använd previewPixels */}
            <MiniPreview
              pixels={props.previewPixels}  // ÄNDRAT
              colors={props.colors}
              secondaryColors={PICO8_SECONDARY}
              scale={4}
              hidden={isMobile}
            />
            <ColorPalette
              colors={paletteForView}
              selectedIndex={selectedIndexForView}
              onSelect={handleSelectColor}
              secondary={showSecondary}
              onToggleSecondary={onToggleSecondary}
            />
          </div>
        )}

        {/* Host som gridden mäter och följer */}
        <div className="editor-main">
          <div className="grid-host" ref={gridHostRef}>
            {props.pixels && (
              <PixelGrid
                pixels={props.pixels}   
                colors={props.colors}
                secondaryColors={PICO8_SECONDARY}
                pixelSize={pixelSize16}
                onPixelClick={handleCanvasClick}
                onPointerDown={handlePointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={handlePointerUp}
                overlay={overlay}
              />
            )}
          </div>
        </div>
      </div>

      {/* Desktop tools‑bar med Undo/Redo inbyggt */}
      <div className="desktop-tools-bar">
        <EditorTools
          variant="desktop"
          onSave={handleSave}
          onMergeP8={props.onMergeP8}
          onRotate={props.onRotate}
          onFlip={props.onFlip}
          onShiftRight={props.onShiftRight}
          onShiftDown={props.onShiftDown}
          onClear={props.onClear}
          saving={saving}
          canSave={canSave}
          onUndo={props.onUndo}
          onRedo={props.onRedo}
          canUndo={!!props.canUndo}
          canRedo={!!props.canRedo}
          gridSize={props.gridSize}
          onToggleGridSize={props.onToggleGridSize}
          onSelectFill={toggleFill}
          fillActive={tool === 'fill'}
        />
      </div>

      {/* Mobil tools‑sheet (Undo/Redo ligger i top‑bar, inte här) */}
      {isMobile && (
        <div className={`tools-sheet ${toolsOpen ? 'open' : ''}`}>
          <EditorTools
            variant="mobile"
            onSave={handleSave}
            onRotate={props.onRotate}
            onFlip={props.onFlip}
            onShiftRight={props.onShiftRight}
            onShiftDown={props.onShiftDown}   // NEW
            onClear={props.onClear}
            saving={saving}
            canSave={canSave}
            onSelectFill={toggleFill}
            fillActive={tool === 'fill'}
          />
        </div>
      )}

      {showSaveDialog && (
        <SaveDialog
          onClose={closeSaveDialog}
          onConfirm={(name) => confirmSave(name)} // skicka vidare namnet
        />
      )}
      {showAuth && <AuthModal onClose={closeAuth} onSuccess={handleAuthSuccess} />}
      <MatchPanelHeights />
      {/* Portal-overlay: visas endast i mobil */}
      {isMobile && (
        <PreviewOverlayPortal
          visible={showMobilePreview}
          pixels={props.overlayPixels}
          colors={props.colors}
          secondaryColors={PICO8_SECONDARY}
          scale={8}
        />
      )}
    </div>
  );
};